import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ArrowRight, ExternalLink, X } from "lucide-react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Event logos
const EVENT_LOGOS = {
  acadelite: "/assets/images/logos/acadelite-gallery.png",
  inspire: "/assets/images/logos/inspire.png",
  edvantage: "/assets/images/logos/edvantage.png",
  sparc: "/assets/images/logos/sparc.png"
};

// Gallery images organized by event (4-5 per event)
const EVENT_GALLERIES = {
  acadelite: {
    name: "AcadElite",
    logo: "/assets/images/logos/acadelite-gallery.png",
    kwikpicLink: "#", // Will be provided later
    images: [
      { url: "/assets/images/gallery/gallery5.jpg", caption: "AcadElite Awards - Major General TPS Waraich" },
      { url: "/assets/images/gallery/gallery7.jpg", caption: "AcadElite Awards - Distinguished Guests" },
      { url: "/assets/images/gallery/gallery1.jpg", caption: "AcadElite Awards 2025 - Lamp Lighting Ceremony" },
      { url: "/assets/images/gallery/gallery8.jpg", caption: "AcadElite Awards - Jyoti Roy Award Ceremony" },
    ]
  },
  edvantage: {
    name: "EdVantage",
    logo: "/assets/images/logos/edvantage.png",
    kwikpicLink: "#", // Will be provided later
    images: [
      { url: "/assets/images/gallery/gallery2.jpg", caption: "EdVantage - Grand Audience" },
      { url: "/assets/images/gallery/gallery9.jpg", caption: "EdVantage - Award Ceremony" },
      { url: "/assets/images/gallery/gallery10.jpg", caption: "EdVantage - X-Factor School Panel" },
      { url: "/assets/images/gallery/gallery11.jpg", caption: "EdVantage - Keynote Speaker" },
    ]
  },
  inspire: {
    name: "INSPIRE",
    logo: "/assets/images/logos/inspire.png",
    kwikpicLink: "#", // Will be provided later
    images: [
      { url: "/assets/images/gallery/gallery6.jpg", caption: "INSPIRE Agra - Panel Discussion" },
      { url: "/assets/images/gallery/gallery12.jpg", caption: "INSPIRE Agra - Expert Panel" },
      { url: "/assets/images/gallery/gallery3.jpg", caption: "INSPIRE Agra - Disruption & Dreams Panelists" },
      { url: "/assets/images/gallery/gallery13.jpg", caption: "INSPIRE Agra - Speaker Session" },
    ]
  }
};

// Full screen image modal
const ImageModal = ({ image, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
        >
          <X size={24} />
        </button>
        
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.9 }}
          className="max-w-5xl max-h-[80vh] relative"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={image.url}
            alt={image.caption}
            className="max-w-full max-h-[80vh] object-contain rounded-lg"
          />
          <p className="text-white text-center mt-4 text-lg">{image.caption}</p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Event Gallery Section Component
const EventGallerySection = ({ eventKey, event }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <div className="mb-16">
      {/* Event Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <img src={event.logo} alt={event.name} className="h-16 object-contain" />
          <h3 className="font-heading text-2xl text-slate-800">{event.name}</h3>
        </div>
        <a
          href={event.kwikpicLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-700 hover:text-red-600 font-medium inline-flex items-center gap-2 text-sm"
        >
          View More <ExternalLink size={14} />
        </a>
      </div>

      {/* Images Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {event.images.map((image, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
            className="aspect-square rounded-2xl overflow-hidden cursor-pointer group relative"
            onClick={() => setSelectedImage(image)}
          >
            <img
              src={image.url}
              alt={image.caption}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
              <p className="text-white text-sm font-medium line-clamp-2">{image.caption}</p>
            </div>
            {/* Click indicator */}
            <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <ExternalLink size={14} className="text-slate-800" />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Image Modal */}
      <ImageModal
        image={selectedImage || {}}
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
      />
    </div>
  );
};

const GalleryPage = () => {
  return (
    <div className="pt-24">
      {/* Hero */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-yellow-50 via-white to-blue-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Memorable Moments</span>
            <h1 className="font-heading text-4xl md:text-6xl font-semibold mt-4 mb-6 text-slate-800">
              Event <span className="gradient-text">Gallery</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Capturing the transformative moments from GSLC's learning forums across India. 
              Click on any image to view full size.
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Gallery by Events */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          {Object.entries(EVENT_GALLERIES).map(([key, event]) => (
            <motion.div key={key} variants={fadeInUp}>
              <EventGallerySection eventKey={key} event={event} />
            </motion.div>
          ))}
        </div>
      </AnimatedSection>

      {/* CTA - Changed from "Join GSLC" */}
      <AnimatedSection className="section-padding bg-gradient-to-r from-slate-50 via-white to-slate-50">
        <div className="container-custom">
          <motion.div 
            variants={fadeInUp}
            className="bg-gradient-to-r from-yellow-500 via-red-600 to-blue-800 rounded-3xl p-12 text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Be Part of Our Next Event
            </h2>
            <p className="text-white/90 mb-8 max-w-xl mx-auto">
              Attend our learning forums and create memorable experiences with India's leading school leaders.
            </p>
            <Link to="/learning-forums" className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-white text-blue-900 font-semibold hover:bg-yellow-400 transition-colors">
              Explore Learning Forums <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default GalleryPage;
